import { formatUrl } from 'frontend-utils/utils/url/format-url';
import ApplicationAdapter from 'lumen/adapters/application';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export interface BulkUserUpdate {
  products?: string[];
  role?: string;
  user_id: number;
}

export interface BulkApplyBody {
  add_products?: string[];
  remove_products?: string[];
  role?: string;
}

export interface BulkApplyFilters {
  product?: string;
  q?: string;
  role?: string;
}

// In exclude mode `subscription_ids` are the subscriptions to leave out, so an
// empty list with `exclude` set means every subscription the admin manages.
export interface InviteBody {
  access_right: string;
  accessible_products: string[];
  copy_reports: boolean;
  emails: string[];
  exclude: boolean;
  subscription_ids: number[];
}

export const MAX_INVITE_EMAILS = 100;

export default class SubscriptionUserAdapter extends ApplicationAdapter {
  private buildBulkURL(
    subscriptionId: string,
    params: Record<string, string>,
  ): string {
    return formatUrl(
      `/${APIGATEWAY}/subscriptions/${subscriptionId}/users/bulk/`,
      params,
      this.host,
    );
  }

  private buildFilterParams(filters: BulkApplyFilters): Record<string, string> {
    const params: Record<string, string> = {};

    if (filters.role) {
      params['filter[role]'] = filters.role;
    }
    if (filters.product) {
      params['filter[product]'] = filters.product;
    }
    if (filters.q) {
      params['filter[q]'] = filters.q;
    }

    return params;
  }

  async bulkApply(
    subscriptionId: string,
    body: BulkApplyBody,
    options: { excludedUserIds: number[]; filters: BulkApplyFilters },
  ): Promise<void> {
    const url = this.buildBulkURL(
      subscriptionId,
      this.buildFilterParams(options.filters),
    );
    await this.ajax(url, 'PATCH', {
      data: { ...body, exclude: options.excludedUserIds },
    });
  }

  async bulkDeleteAll(
    subscriptionId: string,
    options: { excludedUserIds: number[]; filters: BulkApplyFilters },
  ): Promise<void> {
    const url = this.buildBulkURL(
      subscriptionId,
      this.buildFilterParams(options.filters),
    );
    // The excluded ids go in the body rather than the query so a large
    // deselection cannot exceed URL length limits.
    await this.ajax(url, 'DELETE', {
      data: { exclude: options.excludedUserIds },
    });
  }

  async bulkUpdate(
    subscriptionId: string,
    users: BulkUserUpdate[],
  ): Promise<void> {
    const url = `${this.host}/${APIGATEWAY}/subscriptions/${subscriptionId}/users/`;
    await this.ajax(url, 'PATCH', { data: { users } });
  }

  async deleteUsers(subscriptionId: string, userIds: number[]): Promise<void> {
    const url = `${this.host}/${APIGATEWAY}/subscriptions/${subscriptionId}/users/`;
    await this.ajax(url, 'DELETE', { data: { user_ids: userIds } });
  }

  async invite(body: InviteBody): Promise<void> {
    const url = `${this.host}/${APIGATEWAY}/users/invitations`;
    await this.ajax(url, 'POST', { data: body });
  }
}
